import { useEffect, useRef } from 'react';
import { getRandomizedInterval } from './get-randomized-interval';

export const DEFAULT_AUTO_INTERVAL = 2000;

interface UseIntervalProps {
  callback: (interval: number) => void;
  interval: 'auto' | number | null;
  defaultAutoInterval?: number;
}

export const useInterval = ({
  callback,
  interval,
  defaultAutoInterval = DEFAULT_AUTO_INTERVAL
}: UseIntervalProps) => {
  const callbackRef = useRef(callback);
  const isAuto = interval === 'auto';

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!interval) {
      return;
    }

    let timeoutId: NodeJS.Timeout;
    let derivedInterval = isAuto ? defaultAutoInterval : interval;

    const tick = () => {
      const nextInterval = isAuto
        ? getRandomizedInterval(derivedInterval)
        : derivedInterval;
      callbackRef.current(nextInterval);
      derivedInterval = nextInterval;

      timeoutId = setTimeout(tick, nextInterval);
    };

    timeoutId = setTimeout(tick, derivedInterval);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [interval, isAuto, defaultAutoInterval]);
};
